import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Calendar, DollarSign, Receipt, TrendingUp } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { UsageRow } from '@runik/shared';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/layout/PageHeader';
import { MetricCard } from '../../components/dashboard/MetricCard';
import { PaymentStatusBadge } from '../../components/payments/PaymentStatusBadge';
import { EmptyState } from '../../components/data/EmptyState';
import { listPayments, type PaymentListRow } from '../../lib/queries/payments';
import { listUsage } from '../../lib/queries/usage';

function Dashboard() {
  const usage = useQuery<UsageRow[]>({
    queryKey: ['usage', { customerId: undefined }],
    queryFn: () => listUsage(),
  });
  const payments = useQuery({
    queryKey: ['payments', { page: 1, limit: 5 }],
    queryFn: () => listPayments({ page: 1, limit: 5 }),
  });

  const allPayments = useQuery({
    queryKey: ['payments-summary'],
    queryFn: () => listPayments({ page: 1, limit: 100 }),
  });

  const outstanding = (allPayments.data?.data ?? [])
    .filter((p: PaymentListRow) => p.status === 'pending' || p.status === 'overdue')
    .reduce((sum, p) => sum + Number(p.amount), 0);

  const nextDue = (allPayments.data?.data ?? [])
    .filter((p) => p.status === 'pending')
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())[0];

  const monthlyCost = (() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    return (usage.data ?? [])
      .filter((r) => new Date(r.periodStart) >= monthStart)
      .reduce((sum, r) => sum + Number(r.costUsd), 0);
  })();

  const chartData = (usage.data ?? [])
    .slice(0, 30)
    .reverse()
    .map((r) => ({
      day: new Date(r.periodStart).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' }),
      cost: Number(r.costUsd),
    }));

  return (
    <>
      <PageHeader title="Panel" description="Hesap özeti" />
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            label="Bekleyen Borç"
            value={outstanding > 0 ? `${outstanding.toFixed(2)}` : '0'}
            icon={DollarSign}
            hint={outstanding > 0 ? 'beklemede + vadesi geçmiş' : 'Borç yok'}
          />
          <MetricCard
            label="Sıradaki Vade"
            value={nextDue ? format(new Date(nextDue.dueDate), 'd MMM yyyy', { locale: tr }) : '—'}
            icon={Calendar}
            hint={nextDue ? `${nextDue.amount} ${nextDue.currency} — ${nextDue.invoiceNo}` : 'Bekleyen ödeme yok'}
          />
          <MetricCard
            label="Bu Ay API Maliyeti"
            value={`$${monthlyCost.toFixed(2)}`}
            icon={TrendingUp}
            hint="Tüm sağlayıcılar"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Son Ödemeler</CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to="/payments">Tümünü Gör</Link>
              </Button>
            </CardHeader>
            <CardContent>
              {(payments.data?.data ?? []).length === 0 ? (
                <EmptyState
                  icon={Receipt}
                  title="Henüz ödemeniz yok"
                  description="Yeni faturalar burada listelenecek."
                />
              ) : (
                <div className="space-y-3">
                  {(payments.data?.data ?? []).map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm">
                      <div>
                        <div className="font-medium">{p.invoiceNo}</div>
                        <div className="text-xs text-slate-500">
                          {format(new Date(p.dueDate), 'd MMM yyyy', { locale: tr })}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">
                          {p.amount} {p.currency}
                        </span>
                        <PaymentStatusBadge status={p.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Son 30 Gün Kullanım</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {chartData.length === 0 ? (
                  <EmptyState
                    icon={TrendingUp}
                    title="Henüz veri yok"
                    description="İlk snapshot sonrası grafik görünecek."
                  />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="cost" fill="#4f46e5" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute('/_app/dashboard')({ component: Dashboard });
